/**
 * Home base layout schema.
 *
 * Everything visible on the home page is data, stored per-user in
 * Firestore at `homeLayouts/{userId}`. The default seed (see
 * `home-defaults.ts`) is applied for any user without a saved layout.
 *
 * Identity rule: every group/tile/widget/item has a stable `id`. Edits
 * mutate-by-id so reorders never lose state.
 */

export type TileColor =
  | 'slate'
  | 'navy'
  | 'forest'
  | 'sage'
  | 'gold'
  | 'amber'
  | 'terracotta'
  | 'plum'
  | 'rose'
  | 'mist'
  | 'ink'
  | 'parchment';

export interface Tile {
  id: string;
  label: string;
  /** App-icon mark — emoji or 1-2 char glyph. */
  glyph?: string;
  /** Uploaded image URL — when present, rendered in place of the glyph. */
  logoUrl?: string;
  color: TileColor;
  /** Either an in-app route (`/documents`) or an absolute URL. */
  href: string;
  /** Open external URLs in a new tab. */
  external?: boolean;
  /** Optional badge count source: 'triage' | 'bills_due' | static number. */
  badge?: { kind: 'static'; value: number } | { kind: 'count'; source: BadgeSource };
}

export type BadgeSource = 'triage' | 'bills_due_week' | 'bills_past_due' | 'documents_total';

export interface TileGroup {
  id: string;
  title: string;
  tiles: Tile[];
  /**
   * When set, the tiles are generated at render time instead of being stored.
   * 'entities' → one tile per active entity, linking to that entity's documents.
   */
  dynamic?: 'entities';
}

export interface SidebarItem {
  id: string;
  label: string;
  href: string;
  icon?: string; // emoji or glyph
  /** Only render if the current user matches one of these roles. Omit = always. */
  roles?: string[];
}

export interface SidebarSection {
  id: string;
  title?: string;
  items: SidebarItem[];
}

export type WidgetKind =
  | 'action_required'
  | 'queue'
  | 'note'
  | 'links'
  | 'metric'
  | 'recent_documents'
  | 'processing_status';

export interface WidgetBase {
  id: string;
  kind: WidgetKind;
  title: string;
}

export interface ActionRequiredWidget extends WidgetBase {
  kind: 'action_required';
  /** Override label "Submitted / Past due". */
  primaryLabel?: string;
  secondaryLabel?: string;
  /** Where the inbox arrow leads. */
  inboxHref: string;
}

export interface QueueWidget extends WidgetBase {
  kind: 'queue';
  /** Source for items shown in the queue. */
  source: 'triage' | 'bills_due' | 'bills_past_due' | 'static';
  limit: number;
  /** Used when source === 'static'. */
  staticItems?: Array<{ id: string; label: string; status: string; href: string }>;
}

export interface NoteWidget extends WidgetBase {
  kind: 'note';
  body: string;
}

export interface LinksWidget extends WidgetBase {
  kind: 'links';
  links: Array<{ id: string; label: string; href: string; external?: boolean }>;
}

export interface MetricWidget extends WidgetBase {
  kind: 'metric';
  source: BadgeSource;
  caption?: string;
}

export interface RecentDocumentsWidget extends WidgetBase {
  kind: 'recent_documents';
  limit: number;
}

export interface ProcessingStatusWidget extends WidgetBase {
  kind: 'processing_status';
}

export type Widget =
  | ActionRequiredWidget
  | QueueWidget
  | NoteWidget
  | LinksWidget
  | MetricWidget
  | RecentDocumentsWidget
  | ProcessingStatusWidget;

export interface HomeLayout {
  version: number;
  /** Display name shown in the sidebar header. */
  brand: { name: string; tagline?: string; markGlyph?: string; logoUrl?: string };
  sidebar: SidebarSection[];
  groups: TileGroup[];
  /**
   * Ids of built-in (default) sidebar sections, sidebar items, or tile groups
   * the user has explicitly deleted. The self-healing migration that re-adds
   * missing defaults skips anything listed here, so deletions of shipped rows
   * (e.g. Admin → Security Actions) stay deleted instead of bouncing back.
   */
  dismissedNav?: string[];
  rightColumn: Widget[];
  /** Top-bar action buttons (e.g. "Ask SFP", "Notes"). */
  topActions: Array<{
    id: string;
    label: string;
    href?: string;
    kind: 'link' | 'ask' | 'notes' | 'edit';
  }>;
}

export const LAYOUT_VERSION = 3;
